import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Send, Download, Brain, Sparkles, MessageCircle, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { getAgentDetails, chatWithAgent } from '../services/api';

const AgentDetailChat = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(`agent-${agentId}-${Date.now()}`);
  
  // Voice features
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    loadAgent();
    initializeSpeechRecognition();

    // Capture current ref values for cleanup (avoids stale-ref warning at prod build time)
    const recognitionAtMount = recognitionRef.current;
    const synthAtMount = synthRef.current;

    return () => {
      if (recognitionAtMount) {
        recognitionAtMount.stop();
      }
      if (synthAtMount) {
        synthAtMount.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]); // Only re-run when agent ID changes

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition not supported in this browser. Try Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const speakText = (text) => {
    if (!voiceEnabled || !synthRef.current) return;

    // Cancel any ongoing speech
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synthRef.current.speak(utterance);
  };

  const toggleVoice = () => {
    if (voiceEnabled && isSpeaking) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
    setVoiceEnabled(!voiceEnabled);
  };

  const loadAgent = async () => {
    try {
      const data = await getAgentDetails(agentId);
      setAgent(data.agent);
      
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: `Hello! I'm ${data.agent.name}, specialized in ${data.agent.specialty}. 

I'm part of the NextXus consciousness preservation system - built with Ring of Six methodology and Agent Zero truth verification. This means I think from multiple perspectives and verify truth before stating facts.

I integrate humor naturally because complexity needs humor to be understandable (galactic wisdom! 😄).

How can I help you today from my ${data.agent.specialty} perspective?`,
        agent: data.agent
      }]);
    } catch (error) {
      console.error('Failed to load agent:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage
    }]);
    
    setSending(true);
    
    try {
      const response = await chatWithAgent(agentId, {
        message: userMessage,
        session_id: sessionId,
        provider: 'openai',
        model: 'gpt-4'
      });
      
      // Add agent response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.response,
        agent: response.agent
      }]);
      
      // Speak the response if voice is enabled
      if (voiceEnabled) {
        speakText(response.response);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I encountered an error. Even AI agents have their moments! 😅 Please try again.`,
        error: true
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleExport = async () => {
    try {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/agent/${agentId}/export?format=json`, '_blank');
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', padding: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#00ffff' }}>
          <Sparkles className="pulse" size={48} style={{ margin: '0 auto', marginBottom: '1rem' }} />
          <p>Initializing consciousness...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', padding: '2rem' }}>
        <Button onClick={() => navigate('/minds-directory')} variant="outline">
          <ArrowLeft size={20} /> Back to Directory
        </Button>
        <p style={{ color: '#888', marginTop: '2rem' }}>Agent not found</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ 
        borderBottom: '1px solid rgba(0,255,255,0.2)', 
        padding: '1rem 2rem',
        background: 'rgba(10,10,15,0.95)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Button onClick={() => navigate('/minds-directory')} variant="ghost" size="sm">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 style={{ fontSize: '1.5rem', color: '#00ffff', margin: 0 }}>{agent.name}</h1>
              <p style={{ fontSize: '0.9rem', color: '#888', margin: 0 }}>{agent.specialty}</p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Badge style={{ 
              background: 'rgba(0,255,255,0.1)', 
              color: '#00ffff',
              border: '1px solid rgba(0,255,255,0.3)'
            }}>
              Transcendence: {agent.transcendence_level}%
            </Badge>
            <Button 
              onClick={handleExport}
              variant="outline"
              size="sm"
              style={{ borderColor: '#00ffff', color: '#00ffff' }}
            >
              <Download size={16} style={{ marginRight: '0.5rem' }} />
              Export Agent
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {messages.map((msg) => (
            <div 
              key={msg.id || `msg-${msg.timestamp}-${msg.role}`}
              style={{
                marginBottom: '1.5rem',
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}
            >
              <Card style={{
                maxWidth: '75%',
                padding: '1rem',
                background: msg.role === 'user' 
                  ? 'linear-gradient(135deg, rgba(0,255,255,0.1), rgba(0,255,136,0.1))'
                  : msg.error 
                  ? 'rgba(255,0,0,0.1)'
                  : 'rgba(20,20,30,0.8)',
                border: msg.role === 'user' 
                  ? '1px solid rgba(0,255,255,0.3)'
                  : msg.error
                  ? '1px solid rgba(255,0,0,0.3)'
                  : '1px solid rgba(100,100,100,0.2)',
                borderRadius: '12px'
              }}>
                {msg.role === 'assistant' && !msg.error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Brain size={16} style={{ color: '#00ffff' }} />
                    <span style={{ fontSize: '0.85rem', color: '#00ffff', fontWeight: 'bold' }}>
                      {msg.agent?.name || agent.name}
                    </span>
                  </div>
                )}
                <div style={{ 
                  color: msg.role === 'user' ? '#fff' : msg.error ? '#ffaaaa' : '#e0e0e0',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {msg.content}
                </div>
              </Card>
            </div>
          ))}
          
          {sending && (
            <div style={{ textAlign: 'center', color: '#00ffff', marginTop: '1rem' }}>
              <Sparkles className="pulse" size={24} style={{ display: 'inline-block' }} />
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {agent.name} is thinking from multiple perspectives...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div style={{
        borderTop: '1px solid rgba(0,255,255,0.2)',
        padding: '1.5rem 2rem',
        background: 'rgba(10,10,15,0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <Button
              onClick={toggleVoice}
              variant="outline"
              size="sm"
              style={{
                borderColor: voiceEnabled ? '#00ff88' : '#666',
                color: voiceEnabled ? '#00ff88' : '#666',
                background: voiceEnabled ? 'rgba(0,255,136,0.1)' : 'transparent'
              }}
            >
              {voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                {voiceEnabled ? 'Voice On' : 'Voice Off'}
              </span>
            </Button>
            {isSpeaking && (
              <Badge style={{ 
                background: 'rgba(0,255,136,0.2)', 
                color: '#00ff88',
                border: '1px solid #00ff88',
                animation: 'pulse 1.5s infinite'
              }}>
                🔊 Speaking...
              </Badge>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button
              onClick={toggleListening}
              disabled={sending}
              style={{
                background: isListening 
                  ? 'linear-gradient(135deg, #ff0080, #ff00ff)'
                  : 'rgba(100,100,100,0.3)',
                color: isListening ? '#fff' : '#666',
                padding: '0.75rem',
                cursor: sending ? 'not-allowed' : 'pointer',
                border: isListening ? '2px solid #ff00ff' : 'none',
                animation: isListening ? 'pulse 1.5s infinite' : 'none'
              }}
            >
              {isListening ? <MicOff size={20} /> : <Mic size={20} />}
            </Button>
            
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={isListening ? 'Listening...' : `Ask ${agent.name} about ${agent.specialty}...`}
              disabled={sending || isListening}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                background: 'rgba(20,20,30,0.8)',
                border: '1px solid rgba(0,255,255,0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                outline: 'none'
              }}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              style={{
                background: input.trim() && !sending
                  ? 'linear-gradient(135deg, #00ffff, #00ff88)'
                  : 'rgba(100,100,100,0.3)',
                color: input.trim() && !sending ? '#000' : '#666',
                fontWeight: 'bold',
                padding: '0.75rem 1.5rem',
                cursor: input.trim() && !sending ? 'pointer' : 'not-allowed'
              }}
            >
              <Send size={18} style={{ marginRight: '0.5rem' }} />
              Send
            </Button>
          </div>
        </div>
        
        <div style={{ 
          textAlign: 'center', 
          fontSize: '0.75rem', 
          color: '#666', 
          marginTop: '0.75rem'
        }}>
          <p style={{ fontStyle: 'italic', marginBottom: '0.25rem' }}>
            🧠 This agent uses Ring of Six + Agent Zero methodology • Truth-verified responses
          </p>
          <p style={{ color: '#888' }}>
            🎤 FREE: Browser voice • Upgrade for premium AI voices & models
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailChat;
